import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "./client";
import type { Runner, ScenarioSet, Suite, SuiteCreate } from "./types";

const SUITES = ["suites"];

const invalidateSuites = (qc: ReturnType<typeof useQueryClient>) =>
  qc.invalidateQueries({ queryKey: SUITES });

/**
 * A suite advances through work on the server, so the list and detail views
 * poll. Both stop polling once nothing is live: a finished suite is a static
 * record, and a page left open on one should not keep a request in flight all
 * afternoon.
 */
const LIVE_MS = 4000;

export function useSuites() {
  return useQuery({
    queryKey: SUITES,
    queryFn: async () => (await apiFetch<{ suites: Suite[] }>("/api/v1/suites")).suites,
    refetchInterval: (q) => {
      const rows = q.state.data as Suite[] | undefined;
      const live = rows?.some((s) => s.status === "pending" || s.status === "running");
      return live ? LIVE_MS : false;
    },
  });
}

export function useSuite(id: string | undefined) {
  return useQuery({
    queryKey: [...SUITES, id],
    enabled: !!id,
    queryFn: () => apiFetch<Suite>(`/api/v1/suites/${id}`),
    refetchInterval: (q) => {
      const s = q.state.data as Suite | undefined;
      return s && (s.status === "pending" || s.status === "running") ? LIVE_MS : false;
    },
  });
}

export function useStartSuite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SuiteCreate) =>
      apiFetch<Suite>("/api/v1/suites", { method: "POST", body }),
    onSuccess: () => invalidateSuites(qc),
  });
}

export function useStopSuite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<Suite>(`/api/v1/suites/${id}/stop`, { method: "POST" }),
    onSuccess: () => invalidateSuites(qc),
  });
}

export function useRefireSuite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, failedOnly }: { id: string; failedOnly: boolean }) =>
      apiFetch<Suite>(`/api/v1/suites/${id}/refire?failed_only=${failedOnly}`, {
        method: "POST",
      }),
    onSuccess: () => invalidateSuites(qc),
  });
}

export function useDeleteSuite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/api/v1/suites/${id}`, { method: "DELETE" }),
    onSuccess: () => invalidateSuites(qc),
  });
}

/**
 * Runners are polled too: a phone-home runner's liveness is only ever inferred
 * from its last check-in, so "online" goes stale on its own if nothing refetches.
 */
export function useRunners() {
  return useQuery({
    queryKey: ["runners"],
    queryFn: async () => (await apiFetch<{ runners: Runner[] }>("/api/v1/runners")).runners,
    refetchInterval: 15000,
  });
}

export function useRunner(id: string | undefined) {
  return useQuery({
    queryKey: ["runners", id],
    enabled: !!id,
    queryFn: () => apiFetch<Runner>(`/api/v1/runners/${id}`),
    refetchInterval: 15000,
  });
}

export function useForgetRunner() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/api/v1/runners/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["runners"] }),
  });
}

const SETS = ["scenario_sets"];

export function useScenarioSets() {
  return useQuery({
    queryKey: SETS,
    queryFn: async () =>
      (await apiFetch<{ scenario_sets: ScenarioSet[] }>("/api/v1/scenario_sets"))
        .scenario_sets,
  });
}

export function useSaveScenarioSet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id?: string; name: string; description?: string; scenario_ids: string[] }) =>
      apiFetch<ScenarioSet>(id ? `/api/v1/scenario_sets/${id}` : "/api/v1/scenario_sets", {
        method: id ? "PUT" : "POST",
        body,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: SETS }),
  });
}

export function useDeleteScenarioSet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<void>(`/api/v1/scenario_sets/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: SETS }),
  });
}
